<template>
  <div
    class="drawer__list-item group"
    :class="{
      'is-editing': isBeingEdited || isBeingDeleted,
      'is-loading': isLoading
    }"
    :key="label.id"
    :style="'--label-color:' + label.color"
  >
    <transition name="switch" appear mode="out-in" @after-enter="focusInput()">
      <!-- Display Mode -->
      <div
        v-if="!isBeingEdited && !isBeingDeleted"
        key="label"
        class="flex items-center gap-3 w-full cursor-pointer"
        @click="startEditing()"
      >
        <span class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: label.color }"></span>
        <span class="flex-1 text-sm font-medium text-neutral-700">{{ label.name }}</span>
        <div class="opacity-0 group-hover:opacity-100 transition-opacity">
          <button type="button" class="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded transition-colors" :title="$t('Edit')">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <div
        class="drawer__form w-full"
        v-else-if="isBeingEdited && !isBeingDeleted"
        key="form"
      >
        <div class="drawer__form-row">
          <input
            type="text"
            class="input flex-1"
            v-model="newName"
            @keydown.enter.prevent="handleSave()"
            :placeholder="$t('Label name')"
            ref="input"
          />

          <swatch-color-picker
            v-model="newColor"
            dropdown-position="right"
          />
        </div>

        <div class="drawer__form-actions">
          <button
            type="button"
            class="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
            @click.prevent="deleteConfirmation()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
            {{ $t('Delete') }}
          </button>

          <div class="flex items-center gap-2">
            <button
              type="button"
              class="text-sm text-neutral-600 hover:text-neutral-800 font-medium"
              @click.prevent="handleClose()"
            >
              {{ $t('Cancel') }}
            </button>
            <button
              type="button"
              class="btn-main text-sm px-3 py-1.5"
              @click="handleSave()"
            >
              {{ $t('Save') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation -->
      <div
        class="w-full py-2 text-center"
        v-else-if="isBeingEdited && isBeingDeleted"
        key="delete"
      >
        <p class="text-sm text-neutral-600 mb-3">
          {{ $t('This will remove the label from all entries.') }}
        </p>
        <div class="flex items-center justify-center gap-3">
          <button
            type="button"
            class="btn-secondary text-sm px-3 py-1.5"
            @click.prevent="isBeingDeleted = false"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            type="button"
            class="btn-danger text-sm px-3 py-1.5"
            @click.prevent="destroyLabel()"
          >
            {{ $t('Delete') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import SwatchColorPicker from "../../components/swatch-color-picker.vue";

export default {
  props: ["original-label"],

  data: function() {
    return {
      label: this.originalLabel,
      newName: this.originalLabel.name,
      newColor: this.originalLabel.color,
      isBeingEdited: false,
      isBeingDeleted: false,
      isLoading: false
    };
  },

  mounted: function() {
    window.addEventListener("keydown", e => {
      if (e.key == "Escape") {
        this.isBeingEdited = false;
      }
    });
  },

  components: {
    SwatchColorPicker
  },

  methods: {
    startEditing(e) {
      this.isBeingEdited = true;
    },

    focusInput() {
      if (this.isBeingEdited && !this.isBeingDeleted) {
        this.$nextTick(() => {
          this.$refs.input.focus();
        });
      }
    },

    handleClose() {
      this.isBeingEdited = false;
      this.newName = this.label.name;
      this.newColor = this.label.color;
    },

    handleSave() {
      this.updateLabel();
    },

    deleteConfirmation() {
      this.isBeingDeleted = true;
    },

    destroyLabel() {
      this.isLoading = true;

      Rails.ajax({
        url: "/updates/labels/" + this.label.id,
        type: "DELETE",
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          this.handleClose();
          this.$emit("destroyedLabel", this.label);
          this.isLoading = false;
        },
        error: (data, status) => {
          console.log(data);
          this.isLoading = false;
        }
      });
    },

    updateLabel() {
      this.isLoading = true;

      let params = new FormData();

      params.append("label[name]", this.newName);
      params.append("label[color]", this.newColor);

      Rails.ajax({
        url: "/updates/labels/" + this.label.id,
        type: "PATCH",
        data: params,
        dataType: "json",
        beforeSend: function() {
          return true;
        },
        success: data => {
          this.label.name = this.newName;
          this.label.color = this.newColor;
          this.$emit("updatedLabel", this.label);

          this.isBeingEdited = false;
          this.isLoading = false;
        },
        error: (data, status) => {
          console.log(data);
          this.isLoading = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.switch-enter,
.switch-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.switch-enter-to,
.switch-leave {
  opacity: 1;
  transform: translateY(0);
}

.switch-enter-active,
.switch-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}
</style>
